/**
 * Système de tutoriel guidé pour le Dashboard Restaurant LEMO
 * Tooltips interactifs avec progression sauvegardée
 */
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, HelpCircle, Sparkles, Check, Play } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configuration des étapes du tutoriel
const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    type: 'modal',
    title: 'Bienvenue sur LEMO ! 🎉',
    content: 'Découvrez comment gérer votre commerce en moins de 2 minutes !',
    gifUrl: null, // Peut être ajouté plus tard
    icon: '🚀'
  },
  {
    id: 'navigation',
    type: 'tooltip',
    target: '[data-tutorial="nav-menu"]',
    position: 'bottom',
    title: '📍 Navigation',
    content: 'Accédez rapidement à toutes les sections : commandes, menu, paramètres...',
    highlight: true
  },
  {
    id: 'orders',
    type: 'tooltip',
    target: '[data-tutorial="nav-orders"]',
    position: 'bottom',
    title: '📦 Vos Commandes',
    content: 'C\'est ici que vous verrez toutes vos commandes en temps réel. Une notification sonore vous alertera à chaque nouvelle commande !',
    highlight: true,
    icon: '🔔'
  },
  {
    id: 'menu',
    type: 'tooltip',
    target: '[data-tutorial="nav-menu-link"]',
    position: 'bottom',
    title: '🍽️ Votre Menu',
    content: 'Gérez vos catégories et plats. Ajoutez des photos appétissantes pour attirer plus de clients !',
    highlight: true
  },
  {
    id: 'dish-customization',
    type: 'modal',
    title: '⭐ Personnalisation des Plats',
    content: 'Le secret d\'un bon resto sur LEMO !',
    details: [
      { icon: '🥓', text: 'Supplément bacon (+2€)' },
      { icon: '🧀', text: 'Extra fromage (+1.50€)' },
      { icon: '🌶️', text: 'Sauce au choix (gratuit)' }
    ],
    tip: '💡 Les plats personnalisables génèrent +30% de panier moyen !',
    highlight: true,
    isFeature: true,
    actionLabel: 'Essayer maintenant ✨',
    actionPath: '/restaurant-dashboard/menu'
  },
  {
    id: 'complete',
    type: 'modal',
    title: '🎊 Vous êtes prêt à démarrer !',
    content: 'Vous maîtrisez maintenant les bases de LEMO.',
    checklist: [
      'Navigation maîtrisée',
      'Gestion des commandes',
      'Personnalisation des plats',
      'Paramètres configurés'
    ],
    nextSteps: [
      'Ajoutez votre premier plat avec des options',
      'Activez les notifications push',
      'Complétez votre galerie photos'
    ]
  }
];

// Context pour le tutoriel
const TutorialContext = createContext(null);

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
}

export function TutorialProvider({ children }) {
  const { token, user } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(true); // Default to true to prevent flash
  const [tutorialDisabled, setTutorialDisabled] = useState(false);
  const [loading, setLoading] = useState(true);

  // Charger l'état du tutoriel depuis le serveur
  useEffect(() => {
    const loadTutorialState = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/tutorial/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasSeenTutorial(response.data.has_seen_tutorial);
        setTutorialDisabled(response.data.tutorial_disabled);
        setCurrentStep(response.data.current_step || 0);
        
        // Auto-start si première connexion et pas désactivé
        if (!response.data.has_seen_tutorial && !response.data.tutorial_disabled) {
          setTimeout(() => setIsActive(true), 1000);
        }
      } catch (error) {
        console.error('Error loading tutorial state:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadTutorialState();
  }, [token]);

  // Sauvegarder la progression
  const saveProgress = useCallback(async (step, completed = false, disabled = false) => {
    if (!token) return;
    
    try {
      await axios.post(`${API_URL}/api/tutorial/progress`, {
        current_step: step,
        completed,
        disabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving tutorial progress:', error);
    }
  }, [token]);

  const startTutorial = useCallback((fromStep = 0) => {
    setCurrentStep(fromStep);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    const next = currentStep + 1;
    if (next >= TUTORIAL_STEPS.length) {
      completeTutorial();
    } else {
      setCurrentStep(next);
      saveProgress(next);
    }
  }, [currentStep, saveProgress]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const skipTutorial = useCallback(() => {
    setIsActive(false);
    setHasSeenTutorial(true);
    saveProgress(currentStep, false, false);
  }, [currentStep, saveProgress]);

  const completeTutorial = useCallback(() => {
    setIsActive(false);
    setHasSeenTutorial(true);
    saveProgress(TUTORIAL_STEPS.length - 1, true, false);
  }, [saveProgress]);

  const disableTutorial = useCallback((disabled) => {
    setTutorialDisabled(disabled);
    saveProgress(currentStep, hasSeenTutorial, disabled);
  }, [currentStep, hasSeenTutorial, saveProgress]);

  const value = {
    isActive,
    currentStep,
    totalSteps: TUTORIAL_STEPS.length,
    currentStepData: TUTORIAL_STEPS[currentStep],
    hasSeenTutorial,
    tutorialDisabled,
    loading,
    startTutorial,
    nextStep,
    prevStep,
    skipTutorial,
    completeTutorial,
    disableTutorial,
    steps: TUTORIAL_STEPS
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
      {isActive && <TutorialOverlay />}
    </TutorialContext.Provider>
  );
}

// Composant Overlay du tutoriel
function TutorialOverlay() {
  const { 
    currentStep, 
    totalSteps, 
    currentStepData, 
    nextStep, 
    prevStep, 
    skipTutorial,
    completeTutorial
  } = useTutorial();

  const [targetRect, setTargetRect] = useState(null);

  // Trouver l'élément cible pour les tooltips
  useEffect(() => {
    if (currentStepData?.type === 'tooltip' && currentStepData?.target) {
      const findTarget = () => {
        const element = document.querySelector(currentStepData.target);
        if (element) {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);
        } else {
          setTargetRect(null);
        }
      };
      
      findTarget();
      window.addEventListener('resize', findTarget);
      return () => window.removeEventListener('resize', findTarget);
    } else {
      setTargetRect(null);
    }
  }, [currentStepData]);

  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;

  // Rendu Modal
  if (currentStepData?.type === 'modal') {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={skipTutorial} />
        
        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-in fade-in zoom-in-95 duration-300">
          {/* Close button */}
          <button 
            onClick={skipTutorial}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="text-center">
            {/* Icon or GIF */}
            {currentStepData.isFeature ? (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
            ) : isLastStep ? (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Check className="w-8 h-8 text-white" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Play className="w-8 h-8 text-white" />
              </div>
            )}

            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {currentStepData.title}
            </h2>
            
            <p className="text-slate-600 mb-6">
              {currentStepData.content}
            </p>

            {/* Details (pour personnalisation plats) */}
            {currentStepData.details && (
              <div className="bg-slate-50 rounded-xl p-4 mb-4 text-left">
                <p className="text-sm font-medium text-slate-700 mb-3">Proposez des OPTIONS à vos clients :</p>
                <div className="space-y-2">
                  {currentStepData.details.map((detail, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-slate-600">
                      <span className="text-xl">{detail.icon}</span>
                      <span>{detail.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tip */}
            {currentStepData.tip && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-left">
                <p className="text-sm text-amber-800">{currentStepData.tip}</p>
              </div>
            )}

            {/* Checklist (pour étape finale) */}
            {currentStepData.checklist && (
              <div className="bg-emerald-50 rounded-xl p-4 mb-4 text-left">
                {currentStepData.checklist.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-emerald-700 mb-2">
                    <Check className="w-4 h-4" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Next steps */}
            {currentStepData.nextSteps && (
              <div className="text-left mb-6">
                <p className="text-sm font-medium text-slate-700 mb-2">📌 Prochaines étapes recommandées :</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  {currentStepData.nextSteps.map((step, idx) => (
                    <li key={idx}>• {step}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-1.5 mb-6">
              {Array.from({ length: totalSteps }).map((_, idx) => (
                <div 
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep 
                      ? 'w-6 bg-emerald-500' 
                      : idx < currentStep 
                        ? 'bg-emerald-300' 
                        : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-3">
              {isFirstStep ? (
                <Button
                  variant="ghost"
                  onClick={skipTutorial}
                  className="text-slate-500"
                >
                  Passer →
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Retour
                </Button>
              )}
              
              {currentStepData.actionLabel ? (
                <div className="flex gap-2">
                  <Button
                    onClick={nextStep}
                    variant="outline"
                  >
                    Suivant →
                  </Button>
                  <Button
                    onClick={() => {
                      completeTutorial();
                      if (currentStepData.actionPath) {
                        window.location.href = currentStepData.actionPath;
                      }
                    }}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-1"
                  >
                    <Sparkles className="w-4 h-4" />
                    {currentStepData.actionLabel}
                  </Button>
                </div>
              ) : isLastStep ? (
                <Button
                  onClick={completeTutorial}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1"
                >
                  🚀 Aller au tableau de bord
                </Button>
              ) : (
                <Button
                  onClick={nextStep}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1"
                >
                  {isFirstStep ? '🚀 Commencer le tour' : 'Suivant'}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Disable option on first step */}
            {isFirstStep && (
              <label className="flex items-center justify-center gap-2 mt-4 text-sm text-slate-500 cursor-pointer">
                <input type="checkbox" className="rounded" onChange={(e) => {
                  if (e.target.checked) {
                    skipTutorial();
                  }
                }} />
                Ne plus afficher ce tutoriel
              </label>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Rendu Tooltip
  if (currentStepData?.type === 'tooltip') {
    const tooltipStyle = {};
    let arrowPosition = 'top';
    
    if (targetRect) {
      const padding = 16;
      const tooltipWidth = 320;
      const tooltipHeight = 250;
      
      // Calculate position based on available space
      const spaceBelow = window.innerHeight - targetRect.bottom;
      const spaceRight = window.innerWidth - targetRect.right;
      
      // Default to bottom, but adjust if not enough space
      if (spaceBelow >= tooltipHeight || currentStepData.position === 'bottom') {
        tooltipStyle.top = Math.min(targetRect.bottom + padding, window.innerHeight - tooltipHeight - 20);
        arrowPosition = 'top';
      } else {
        tooltipStyle.bottom = Math.max(window.innerHeight - targetRect.top + padding, 20);
        arrowPosition = 'bottom';
      }
      
      // Horizontal centering with viewport bounds
      const idealLeft = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      tooltipStyle.left = Math.max(20, Math.min(idealLeft, window.innerWidth - tooltipWidth - 20));
      
    } else {
      // Fallback to center if target not found
      tooltipStyle.top = '50%';
      tooltipStyle.left = '50%';
      tooltipStyle.transform = 'translate(-50%, -50%)';
    }

    return createPortal(
      <div className="fixed inset-0 z-[9999]">
        {/* Backdrop with hole for target */}
        <div className="absolute inset-0 bg-black/50" onClick={skipTutorial} />
        
        {/* Highlight target element */}
        {targetRect && currentStepData.highlight && (
          <div 
            className="absolute bg-white rounded-lg shadow-lg ring-4 ring-emerald-400 ring-opacity-50 animate-pulse"
            style={{
              top: targetRect.top - 4,
              left: targetRect.left - 4,
              width: targetRect.width + 8,
              height: targetRect.height + 8,
              pointerEvents: 'none'
            }}
          />
        )}

        {/* Tooltip */}
        <div 
          className="absolute bg-white rounded-xl shadow-2xl p-5 max-w-sm animate-in fade-in slide-in-from-top-2 duration-300"
          style={tooltipStyle}
        >
          {/* Arrow */}
          <div className={`absolute w-3 h-3 bg-white transform rotate-45 ${
            arrowPosition === 'top' ? '-top-1.5 left-1/2 -translate-x-1/2' :
            arrowPosition === 'bottom' ? '-bottom-1.5 left-1/2 -translate-x-1/2' :
            arrowPosition === 'left' ? 'top-1/2 -left-1.5 -translate-y-1/2' :
            'top-1/2 -right-1.5 -translate-y-1/2'
          }`} />

          <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
            {currentStepData.title}
            {currentStepData.icon && <span>{currentStepData.icon}</span>}
          </h3>
          
          <p className="text-slate-600 text-sm mb-4">
            {currentStepData.content}
          </p>

          {/* Progress */}
          <div className="flex items-center justify-center gap-1.5 mb-4">
            {Array.from({ length: totalSteps }).map((_, idx) => (
              <div 
                key={idx}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentStep 
                    ? 'w-4 bg-emerald-500' 
                    : idx < currentStep 
                      ? 'bg-emerald-300' 
                      : 'bg-slate-200'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </Button>
            
            <span className="text-xs text-slate-400">
              {currentStep + 1}/{totalSteps}
            </span>
            
            <Button
              size="sm"
              onClick={nextStep}
              className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1"
            >
              Suivant
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return null;
}

// Bouton d'aide pour relancer le tutoriel
export function TutorialHelpButton() {
  const { startTutorial, tutorialDisabled, disableTutorial, steps, loading } = useTutorial();
  const [isOpen, setIsOpen] = useState(false);

  if (loading) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
        title="Aide & tutoriels"
        data-testid="tutorial-help-btn"
      >
        <HelpCircle className="w-5 h-5" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-slate-100 py-2 w-56 z-50 animate-in fade-in slide-in-from-top-2">
            <div className="px-3 py-2 border-b border-slate-100">
              <p className="font-medium text-slate-900 text-sm">📚 Tutoriels</p>
            </div>
            
            <div className="py-1">
              <button
                onClick={() => {
                  startTutorial(0);
                  setIsOpen(false);
                }}
                className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
              >
                <Play className="w-4 h-4 text-emerald-500" />
                Revoir le tour complet
              </button>
              
              {steps.filter(s => s.type !== 'modal' || s.isFeature).slice(1, -1).map((step, idx) => (
                <button
                  key={step.id}
                  onClick={() => {
                    startTutorial(idx + 1);
                    setIsOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                >
                  {step.isFeature && <Sparkles className="w-4 h-4 text-amber-500" />}
                  {!step.isFeature && <span className="w-4 h-4 flex items-center justify-center text-xs">▶</span>}
                  {step.title.replace(/[📍📦🍽️⭐]/g, '').trim()}
                </button>
              ))}
            </div>

            <div className="border-t border-slate-100 px-3 py-2">
              <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={tutorialDisabled}
                  onChange={(e) => disableTutorial(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Désactiver les tutoriels
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TutorialProvider;
